import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  Save, 
  Loader, 
  Tag,
  Percent,
  DollarSign,
  Gift,
  Calendar,
  Settings,
  Eye,
  Sparkles,
  AlertTriangle,
  Package,
  Users,
  Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DiscountCode } from '../../services/discountService';
import { AffiliateSelect } from '../AffiliateSelect';

interface CreateDiscountCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (discountData: Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

export function CreateDiscountCodeModal({ isOpen, onClose, onSubmit }: CreateDiscountCodeModalProps) {
  // Helper function to convert Date to YYYY-MM-DD format without timezone issues
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage' as 'percentage' | 'fixed' | 'free_shipping',
    value: 10,
    minOrderAmount: 0,
    maxUses: 100,
    currentUses: 0,
    status: 'active' as 'active' | 'inactive' | 'expired',
    startDate: formatDateForInput(new Date()),
    endDate: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days from now
    applicableProducts: ['all'],
    affiliateId: undefined as string | undefined,
    membersOnly: false
  });

  const handleClose = () => {
    if (loading) return;
    onClose();
    // Reset form after animation completes
    setTimeout(() => {
      setFormData({
        code: '',
        description: '',
        type: 'percentage',
        value: 10,
        minOrderAmount: 0,
        maxUses: 100,
        currentUses: 0,
        status: 'active',
        startDate: formatDateForInput(new Date()),
        endDate: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
        applicableProducts: ['all'],
        affiliateId: undefined,
        membersOnly: false
      });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    if (formData.type !== 'free_shipping' && formData.value <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      
      const discountData: Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'> = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim(),
        type: formData.type,
        value: formData.type === 'free_shipping' ? 0 : formData.value,
        minOrderAmount: formData.minOrderAmount,
        maxUses: formData.maxUses,
        currentUses: 0,
        status: formData.status,
        startDate: new Date(formData.startDate + 'T00:00:00'),
        endDate: new Date(formData.endDate + 'T23:59:59'),
        applicableProducts: formData.applicableProducts,
        membersOnly: formData.membersOnly,
        ...(formData.affiliateId && { affiliateId: formData.affiliateId })
      };

      await onSubmit(discountData);
      toast.success('Discount code created successfully!');
      handleClose();
    } catch (error) {
      console.error('Error creating discount code:', error);
      toast.error('Failed to create discount code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent className="h-4 w-4" />;
      case 'fixed':
        return <DollarSign className="h-4 w-4" />;
      case 'free_shipping':
        return <Gift className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Tag className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Create Discount Code
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Set up a new promotional discount code
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClose}
                    disabled={loading}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="p-8 space-y-8 max-h-[calc(90vh-200px)] overflow-y-auto">
              {/* Basic Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center">
                    <Settings className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Discount Code</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SUMMER30"
                      className="h-12 rounded-xl border-gray-200 text-base font-mono"
                      maxLength={20}
                    />
                    <p className="text-xs text-gray-500">Use uppercase letters and numbers only</p>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Discount Type</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value: 'percentage' | 'fixed' | 'free_shipping') => 
                        setFormData({ ...formData, type: value, value: value === 'free_shipping' ? 0 : 10 })
                      }
                    >
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">
                          <div className="flex items-center gap-2">
                            <Percent className="h-4 w-4" />
                            Percentage Off
                          </div>
                        </SelectItem>
                        <SelectItem value="fixed">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Fixed Amount Off
                          </div>
                        </SelectItem>
                        <SelectItem value="free_shipping">
                          <div className="flex items-center gap-2">
                            <Gift className="h-4 w-4" />
                            Free Shipping
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this discount code..."
                    className="rounded-xl border-gray-200 text-base min-h-[80px]"
                    maxLength={200}
                  />
                </div>
              </motion.div>

              {/* Discount Configuration */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Discount Configuration</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {formData.type !== 'free_shipping' && (
                    <div className="space-y-3">
                      <Label className="text-base font-semibold text-gray-900">
                        {formData.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                      </Label>
                      <Input
                        type="number"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                        placeholder={formData.type === 'percentage' ? '10' : '25.00'}
                        className="h-12 rounded-xl border-gray-200 text-base"
                        min="0"
                        max={formData.type === 'percentage' ? 100 : undefined}
                        step={formData.type === 'percentage' ? 1 : 0.01}
                      />
                    </div>
                  )}

                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Minimum Order Amount ($)</Label>
                    <Input
                      type="number"
                      value={formData.minOrderAmount}
                      onChange={(e) => setFormData({ ...formData, minOrderAmount: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="h-12 rounded-xl border-gray-200 text-base"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Maximum Uses</Label>
                    <Input
                      type="number"
                      value={formData.maxUses}
                      onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) || 0 })}
                      placeholder="100"
                      className="h-12 rounded-xl border-gray-200 text-base"
                      min="1"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'active' | 'inactive' | 'expired') => 
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-1 md:col-span-2 border border-amber-100 rounded-2xl p-4 flex items-start justify-between bg-amber-50/60">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center border border-amber-100">
                        <Crown className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <Label className="text-base font-semibold text-gray-900 block">Rush'd Members Only</Label>
                        <p className="text-sm text-gray-600">
                          Limit this discount to active JamPack'd Rush'd members. Non-members will see a message inviting them to join.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.membersOnly}
                      onCheckedChange={(checked) => setFormData({ ...formData, membersOnly: checked })}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Schedule */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="h-12 rounded-xl border-gray-200 text-base"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">End Date</Label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="h-12 rounded-xl border-gray-200 text-base"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Product Applicability - Disabled */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6 opacity-60"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-gray-400 to-gray-500 rounded-lg flex items-center justify-center">
                    <Package className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-500">Product Applicability</h3>
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
                      <AlertTriangle className="h-3 w-3 text-gray-500" />
                      <span className="text-xs text-gray-500 font-medium">Coming Soon</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-500">Applicable Products</Label>
                  <div className="relative">
                    <Select
                      value="all"
                      disabled
                    >
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 text-base bg-gray-50 cursor-not-allowed border-dashed">
                        <SelectValue placeholder="All Products" />
                      </SelectTrigger>
                    </Select>
                    <div className="absolute inset-0 bg-gray-50/50 rounded-xl pointer-events-none"></div>
                  </div>
                  <p className="text-xs text-gray-400 italic">
                    Currently applies to all products. Advanced targeting options coming soon.
                  </p>
                </div>
              </motion.div>

              {/* Affiliate Linking */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Affiliate Linking</h3>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Link to Affiliate</Label>
                  <AffiliateSelect
                    value={formData.affiliateId}
                    onChange={(affiliateId) => setFormData({ ...formData, affiliateId })}
                    placeholder="Select affiliate (optional)"
                    className="h-12"
                  />
                  <p className="text-xs text-gray-500">
                    Link this discount code to an affiliate to track commission attribution. When users apply this code, the linked affiliate will earn commissions from successful orders.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    onClick={handleClose} 
                    disabled={loading}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Create Discount Code
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 
